/**
 * PDP Sticky ATC Bar — enhancements that hook the existing
 * `pdp-sticky-bar` custom element from pdp-sticky-bar.js without
 * modifying it directly.
 *
 *  - Live price updates when the visitor changes a variant
 *    (theme's <variant-picker> fires `variant:change` on the product
 *    form; we read event.detail.variant and reformat the price).
 *  - Better watch-target selection: pick the visible <buy-buttons>
 *    element instead of the first one (the PDP renders an extra
 *    hidden mobile sticky buy-buttons that the original observer
 *    sometimes locked onto, breaking the slide-in trigger).
 *  - Label swap on unavailable variants → "Join Waitlist".
 */

(() => {
  const cents = (n) => Number(n) / 100;
  const SHOP_CURRENCY =
    (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  const LOCALE = document.documentElement.lang || 'en-US';

  const formatMoney = (price) => {
    try {
      return new Intl.NumberFormat(LOCALE, {
        style: 'currency',
        currency: SHOP_CURRENCY,
      }).format(cents(price));
    } catch (_) {
      return `$${cents(price).toFixed(2)}`;
    }
  };

  const init = () => {
    const bar = document.querySelector('pdp-sticky-bar');
    if (!bar) return;
    const labelEl = bar.querySelector('.pdp-sticky-bar__label');
    const priceEl = bar.querySelector('[data-pdp-sticky-price]');

    const mainBuyButtons = document.querySelector('.shopify-section--main-product buy-buttons[data-enable-bis]') || document.querySelector('.shopify-section--main-product buy-buttons');
    const getPrimaryCta = () => {
      if (!mainBuyButtons) return null;
      return Array.from(mainBuyButtons.querySelectorAll('button, a.button')).find((el) =>
        !el.disabled && !el.closest('[hidden]') && el.getClientRects().length > 0
      );
    };
    const syncLabel = (variant) => {
      const cta = getPrimaryCta();
      const isWaitlist = !!cta?.closest('[data-bis-modal-trigger-wrap]');
      const label = isWaitlist ? cta.textContent.trim() : 'Buy Now';
      if (labelEl) labelEl.textContent = label;
      bar.querySelector('[data-pdp-sticky-trigger]')?.setAttribute('aria-label', label);
      if (priceEl) {
        priceEl.hidden = isWaitlist;
        if (isWaitlist) priceEl.textContent = '';
        else if (variant?.price != null) priceEl.textContent = formatMoney(variant.price);
      }
    };
    syncLabel();
    // The shared buy-buttons component owns stock and market availability.
    // Observe its state so initialization and variant changes use the same CTA.
    if (mainBuyButtons) {
      const observer = new MutationObserver(() => syncLabel());
      observer.observe(mainBuyButtons, { attributes: true, attributeFilter: ['hidden', 'disabled'], childList: true, subtree: true });
      document.getElementById(mainBuyButtons.getAttribute('form'))?.addEventListener('variant:change', (event) => syncLabel(event.detail?.variant));
    }

    // Replace the original click handler with a more targeted one. The
    // original picks the FIRST match from a global selector list, which
    // can grab a hidden submit button on the page instead of the actual
    // visible CTA inside the main <buy-buttons> — particularly an issue
    // for waitlist-state products where the original handler clicks the
    // wrong element (or nothing).
    //
    // Capture-phase + stopImmediatePropagation runs before the original
    // bubble-phase handler and prevents it from firing. Then we locate
    // the right CTA inside the currently-visible <buy-buttons> and click
    // it directly — which dispatches the right event for both Add to
    // Cart (form submit) and Join Waitlist (drawer aria-controls).
    const stickyTrigger = bar.querySelector('[data-pdp-sticky-trigger]');
    if (stickyTrigger) {
      // Inject the spinner ring next to label + price. Doing this from JS
      // because the original liquid snippet is sealed (macOS provenance)
      // and we can't edit its markup directly.
      if (!stickyTrigger.querySelector('.pdp-sticky-bar__spinner')) {
        const spinner = document.createElement('span');
        spinner.className = 'pdp-sticky-bar__spinner';
        spinner.setAttribute('aria-hidden', 'true');
        stickyTrigger.appendChild(spinner);
      }

      // Loading-state helpers — flips the .is-loading class which the CSS
      // uses to slide label + price into the centre, fade them out, and
      // bloom in the spinning ring.
      const setLoading = (state) => {
        stickyTrigger.classList.toggle('is-loading', !!state);
        if (state) {
          stickyTrigger.setAttribute('aria-busy', 'true');
        } else {
          stickyTrigger.removeAttribute('aria-busy');
        }
      };

      // Listen for the events the theme fires after a successful cart add
      // (and the BIS drawer open) so we can clear the loading state once
      // the action lands. The 2.4s timeout is a safety net for the case
      // where nothing fires — the sticky reverts gracefully instead of
      // getting stuck spinning forever.
      let loadingTimer = null;
      const clearLoading = () => {
        if (loadingTimer) {
          clearTimeout(loadingTimer);
          loadingTimer = null;
        }
        setLoading(false);
      };
      document.addEventListener('cart:refresh', clearLoading);
      document.addEventListener('cart:add', clearLoading);
      document.addEventListener('drawer:opened', clearLoading);

      stickyTrigger.addEventListener(
        'click',
        (event) => {
          event.stopImmediatePropagation();
          event.preventDefault();

          // Double-click guard — if we're already loading, ignore the tap.
          if (stickyTrigger.classList.contains('is-loading')) return;

          const cta = getPrimaryCta();
          if (!cta) return;

          setLoading(true);
          loadingTimer = setTimeout(clearLoading, 2400);
          cta.click();
        },
        true
      );
    }

    // If multiple <buy-buttons> exist and the original observer locked onto
    // a hidden one, the slide-in trigger will never fire. Re-observe the
    // currently-visible one and forward show/hide via class toggles on the
    // existing custom element. Safe to run even if the original observer
    // is already correct — it just adds a second redundant trigger.
    const candidates = Array.from(document.querySelectorAll('buy-buttons'));
    const visible = candidates.find((el) => {
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0 && getComputedStyle(el).visibility !== 'hidden';
    });
    if (visible && 'IntersectionObserver' in window) {
      // Use a small negative rootMargin on the bottom so the sticky doesn't
      // flicker on/off when the buy-buttons sit exactly at the viewport
      // boundary during slow scrolls. Effectively: only show when the buy
      // buttons are at least ~24px above the viewport.
      const io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting || entry.intersectionRatio > 0) {
              bar.classList.remove('is-visible', 'is-revealed');
              bar.setAttribute('aria-hidden', 'true');
            } else if (entry.boundingClientRect.top < 0) {
              if (!bar.classList.contains('is-visible')) {
                bar.classList.add('is-visible');
                bar.setAttribute('aria-hidden', 'false');
              }
            }
          }
        },
        { threshold: 0, rootMargin: '-24px 0px 0px 0px' }
      );
      io.observe(visible);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
